// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { useCallback } from 'react';
import { useApp } from '../store/AppContext.js';
import { pushBlob } from '../lib/syncClient.js';
import type { LedgerEvent } from '../types.js';

/** Encrypt a batch of events into a single blob via the crypto worker. */
function encryptEventsToBlob(
  worker: Worker,
  keyData: ArrayBuffer,
  events: LedgerEvent[],
): Promise<number[]> {
  return new Promise<number[]>((resolve, reject) => {
    const id = `encrypt-${Date.now()}-${Math.random()}`;
    const handler = (e: MessageEvent) => {
      const msg = e.data as { type: string; id: string; ciphertext?: number[]; message?: string };
      if (msg.id !== id) return;
      worker.removeEventListener('message', handler);
      if (msg.type === 'ENCRYPT_RESULT' && msg.ciphertext) {
        resolve(msg.ciphertext);
      } else {
        reject(new Error(msg.message ?? 'Encryption failed'));
      }
    };
    worker.addEventListener('message', handler);
    worker.postMessage({ type: 'ENCRYPT', id, events, keyData });
  });
}

/**
 * Returns a function that dispatches an event locally, caches it in IndexedDB,
 * and (if connected) encrypts and pushes it to the sync server.
 */
export function useEncryptAndPush(worker: Worker | null, keyData: ArrayBuffer | null) {
  const { dispatch, state } = useApp();
  const { auth } = state;

  return useCallback(
    async (event: LedgerEvent): Promise<void> => {
      dispatch({ type: 'EVENT_APPENDED', event });
      // The encrypted snapshot is persisted by MainApp's effect (SEC-05).

      if (auth.token && keyData && worker) {
        const ciphertext = await encryptEventsToBlob(worker, keyData, [event]);
        await pushBlob({ baseUrl: auth.syncUrl, token: auth.token }, new Uint8Array(ciphertext));
      }
    },
    [dispatch, auth, worker, keyData],
  );
}

/**
 * Batch variant of useEncryptAndPush: dispatches every event locally, caches
 * them all to IndexedDB, then (if connected) encrypts the whole array into a
 * single blob and pushes it in one request — one flush, not one round-trip per
 * event. Used by the first-run wizard to seed a starter chart of accounts.
 *
 * Events must already carry consecutive sequences (e.g. lastSequence + 1..N);
 * they are pushed in the order given.
 */
export function useEncryptAndPushBatch(worker: Worker | null, keyData: ArrayBuffer | null) {
  const { dispatch, state } = useApp();
  const { auth } = state;

  return useCallback(
    async (events: LedgerEvent[]): Promise<void> => {
      if (events.length === 0) return;

      for (const event of events) {
        dispatch({ type: 'EVENT_APPENDED', event });
      }
      // The encrypted snapshot is persisted by MainApp's effect (SEC-05).

      if (auth.token && keyData && worker) {
        const ciphertext = await encryptEventsToBlob(worker, keyData, events);
        await pushBlob({ baseUrl: auth.syncUrl, token: auth.token }, new Uint8Array(ciphertext));
      }
    },
    [dispatch, auth, worker, keyData],
  );
}
